import Dialog from '@pelckmans/elementis/components/dialog';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../contexts/ModalContext';
import Tabs from '@pelckmans/elementis/components/tabs';
import StandardAvatarTab from '../components/StandardAvatarTab';
import Button from '@pelckmans/elementis/components/button';
import UserImagesContext from '../../../contexts/UserImagesContext';
import { createUploadRequestForCustomAvatar, updatePersonalInformation } from '../../../api/my-account';
import { useDefaultToast } from '../../../hooks/useDefaultToast';
import CustomAvatarTab from '../components/CustomAvatarTab';
import { Area } from 'react-easy-crop';
import getCroppedFile from '../../../utils/CropFile';
import axios from 'axios';

interface Props {
  id: string;
}

const TABS = {
  STANDARD: 'STANDARD',
  CUSTOM: 'CUSTOM',
} as const;

export type FileData = {
  file: File;
  url: string;
};

const BASE = 'PAGES.PROFILE.MODALS.SELECT_AVATAR';
const STANDARD_AVATAR_PATH = '/images/avatar/';

export default function SelectAvatarModal({ id }: Props) {
  const { closeModal } = useContext(ModalContext);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [customAvatar, setCustomAvatar] = useState<FileData | undefined>();
  const { updateImage, avatar } = useContext(UserImagesContext);
  const { failedToast } = useDefaultToast();
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showAlert, setShowAlert] = useState(false);

  const isStandardImage = avatar.includes(STANDARD_AVATAR_PATH);
  const selectedTab = isStandardImage ? TABS.STANDARD : TABS.CUSTOM;
  const [tab, setTab] = useState<string>(selectedTab);

  const [standardAvatar, setStandardAvatar] = useState<string | undefined>(isStandardImage ? avatar : undefined);

  const handleStandardImage = async () => {
    if (!standardAvatar) return setShowAlert(true);
    setLoading(true);

    try {
      await updatePersonalInformation({ avatar: { predefinedImage: standardAvatar.split(STANDARD_AVATAR_PATH)[1] } });

      updateImage({ avatar: standardAvatar });
      closeModal(id);
    } catch {
      failedToast();
    } finally {
      setLoading(false);
    }
  };

  const handleCustomImage = async () => {
    if (!customAvatar || !croppedAreaPixels) {
      return setErrorMessage(t(`${BASE}.ERROR.NO_FILE`));
    }

    setLoading(true);

    try {
      const croppedFileDataUrl = await getCroppedFile(customAvatar.url, customAvatar.file.type, croppedAreaPixels);
      if (!croppedFileDataUrl) return;

      const croppedFile = await (await fetch(croppedFileDataUrl)).arrayBuffer();
      const { id: avatarId, uploadUrl } = await createUploadRequestForCustomAvatar();

      await axios.put(uploadUrl, croppedFile, { headers: { 'Content-Type': customAvatar.file.type } });

      await updatePersonalInformation({ avatar: { customImage: avatarId } });

      updateImage({ avatar: croppedFileDataUrl });
      closeModal(id);
    } catch {
      failedToast();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (tab === TABS.STANDARD) {
      return handleStandardImage();
    } else if (tab === TABS.CUSTOM) {
      return handleCustomImage();
    }
  };

  useEffect(() => {
    if (standardAvatar) setShowAlert(false);
  }, [standardAvatar]);

  return (
    <Dialog id={id} aria-label={t(`${BASE}.A11Y_LABEL`)} modal size="sm" onClose={() => closeModal(id)}>
      <Tabs onTabChange={tab => setTab(tab)} selectedTab={tab}>
        <Dialog.Header title={t(`${BASE}.HEADER.TITLE`)} onClose={() => closeModal(id)} aria-label={t('ACTIONS.CLOSE')}>
          <Tabs.TabList aria-label="tab-list" fluid>
            <Tabs.Tab id={TABS.STANDARD}>{t(`${BASE}.TABS.STANDARD.TAB`)}</Tabs.Tab>
            <Tabs.Tab id={TABS.CUSTOM}>{t(`${BASE}.TABS.CUSTOM.TAB`)}</Tabs.Tab>
          </Tabs.TabList>
        </Dialog.Header>
        <Tabs.TabsContent>
          <Tabs.TabPanel id={TABS.STANDARD}>
            <StandardAvatarTab
              standardAvatar={standardAvatar}
              setStandardAvatar={setStandardAvatar}
              showAlert={showAlert}
            />
          </Tabs.TabPanel>
          <Tabs.TabPanel id={TABS.CUSTOM}>
            <CustomAvatarTab
              customAvatar={customAvatar}
              setCustomAvatar={setCustomAvatar}
              setCroppedAreaPixels={setCroppedAreaPixels}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
            />
          </Tabs.TabPanel>
        </Tabs.TabsContent>
      </Tabs>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              <Button key="button-cancel" prominence="neutral" onClick={() => closeModal(id)}>
                {t('ACTIONS.CANCEL')}
              </Button>
              <Button key="button-apply" disabled={loading} prominence="success" onClick={handleConfirm}>
                {t('ACTIONS.APPLY')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
